import React, { useEffect, useState } from "react";
import {
    Table,
    TableCell,
    TableBody,
    TableHead,
    TableRow,
    Button,
    View,
    Flex,
    Text,
    Pagination
} from "@aws-amplify/ui-react";
import { FiMail, FiRefreshCw } from "react-icons/fi";
import { useNavigate, useParams } from "react-router-dom";
import { get, del } from 'aws-amplify/api';
import { Cache } from 'aws-amplify/utils';
import { LuMousePointerClick } from "react-icons/lu";
import { MdDeleteForever } from "react-icons/md";
import moment from "moment";

const formatEmailDisplay = (item: any): string => {
    if (item.source) {
        return item.source;
    }
    if (item.source_display_name && item.source_email) {
        return `${item.source_display_name} <${item.source_email}>`;
    }
    if (item.source_email) {
        return item.source_email;
    }
    if (item.commonHeaders?.from?.[0]) {
        return item.commonHeaders.from[0];
    }
    if (item.commonHeaders?.sender) {
        return item.commonHeaders.sender;
    }
    if (item.commonHeaders?.returnPath) {
        return item.commonHeaders.returnPath;
    }
    return 'Unknown Sender';
};

const EmailMessages = () => {
    const cacheExpirationDuration = 5 * 60 * 1000;
    const [messages, setMessages] = useState<any[]>([]);
    const [error, setError] = useState<unknown>(null);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    const navigate = useNavigate();
    const params = useParams();
    const emailAddress = params.addressId;

    const handleBackClick = () => {
        navigate(`/email-accounts/`);
    };

    async function handleDeleteAddress(emailAddress) {
        try {
            setLoading(true)
            const restOperation = del({
                apiName: 'disposible',
                path: `addresses/${emailAddress}/`,
            });
            await restOperation.response;
            console.log('DELETE call succeeded');
            handleBackClick();
        } catch (err) {
            console.error('DELETE call failed: ', err);
            setError(err);
        } finally {
            setLoading(false)
        }
    }

    async function handleDeleteMessage(event, emailAddress, messageId) {
        event.preventDefault();
        event.stopPropagation();
        try {
            setLoading(true)
            const restOperation = del({
                apiName: 'disposible',
                path: `addresses/${emailAddress}/${messageId}`,
            });
            await restOperation.response;

            setMessages((prevMessages) => prevMessages.filter(message => message.messageId !== messageId));

            console.log('DELETE call succeeded');
        } catch (err) {
            console.error('DELETE call failed: ', err);
            setError(err);
        } finally {
            setLoading(false)
        }
    }

    async function getMessages(emailAddress: string, useCache = false) {
        setLoading(true);
        const cacheKey = `messagesCache_${emailAddress}`;

        try {
            if (useCache) {
                const cachedData = await Cache.getItem(cacheKey);
                if (cachedData) {
                    setMessages(cachedData);
                    setLoading(false);
                    return;
                }
            }

            const restOperation = get({
                apiName: 'disposible',
                path: `addresses/${emailAddress}`,
            });
            const { body } = await restOperation.response;
            const response = await body.json();

            if (Array.isArray(response)) {
                setMessages(response);
                Cache.setItem(cacheKey, response, { expires: new Date().getTime() + cacheExpirationDuration });
            }
        } catch (err) {
            console.error('GET call failed: ', err);
            setError(err);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        getMessages(emailAddress);
    }, [emailAddress]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleMessageClick = (emailAddress, messageId) => {
        navigate(`/email-accounts/${encodeURIComponent(emailAddress)}/${messageId}`);
    };

    // Pagination calculations
    const totalPages = Math.ceil(messages.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentMessages = messages.slice(startIndex, endIndex);

    const handlePageChange = (newPage: number) => {
        setCurrentPage(newPage);
    };

    if (loading) return <div>Loading...</div>;
    if (error) return <div>Error fetching messages</div>;

    return (
        <>
            <Flex className="header-flex">
                <div className="email-header">
                    <h2>Email Messages: {emailAddress}</h2>
                </div>
            </Flex>
            <Flex
                justifyContent="flex-start"
                alignItems="center"
                gap="small"
            >
                <Button variation="destructive" onClick={() => handleDeleteAddress(emailAddress)} size="small">Delete</Button>
                <Button onClick={() => getMessages(emailAddress, false)} variation="primary">
                    <FiRefreshCw />
                </Button>
                <div style={{ flex: 1 }}></div>
                <Button onClick={() => handleBackClick()} justifyContent="flex-end" size="small">Back</Button>
            </Flex>
            <Flex justifyContent="normal"
                alignItems="center"
                alignContent="flex-start">
                <View as="div"
                    backgroundColor="var(--amplify-colors-white)"
                    boxShadow="3px 3px 5px 6px var(--amplify-colors-neutral-60)"
                    borderRadius="6px"
                    marginTop="1rem"
                    width="100%"
                >
                    <Table highlightOnHover={true} className="responsive-table">
                        <TableHead>
                            <TableRow>
                                <TableCell as="th">From</TableCell>
                                <TableCell as="th">Subject</TableCell>
                                <TableCell as="th">Date</TableCell>
                                <TableCell as="th" style={{ textAlign: "center" }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {currentMessages.map((item) => (
                                <TableRow
                                    key={item.messageId}
                                    onClick={() =>
                                        handleMessageClick(emailAddress as string, item.messageId)
                                    }>
                                    <TableCell data-label="From">
                                        <Flex style={{ alignItems: 'center' }}>
                                            {!item.is_read && <FiMail aria-label="unread"  />}
                                            <Text title={formatEmailDisplay(item)}>
                                                {formatEmailDisplay(item)}
                                            </Text>
                                        </Flex>
                                    </TableCell>

                                    <TableCell data-label="Subject" >
                                        <Text title={item.commonHeaders?.subject || "(no subject)"}>
                                            {item.commonHeaders?.subject || "(no subject)"}
                                        </Text>
                                    </TableCell>

                                    <TableCell data-label="Date">
                                        {moment(item.commonHeaders.date).calendar()}
                                    </TableCell>

                                    <TableCell data-label="Actions">
                                        <Flex justifyContent="right" gap="small">
                                            <Button
                                                size="small"
                                                colorTheme="error"
                                                onClick={(e) =>
                                                    handleDeleteMessage(
                                                        e,
                                                        emailAddress as string,
                                                        item.messageId
                                                    )
                                                }>
                                                <MdDeleteForever />Delete
                                            </Button>
                                            <Button
                                                size="small"
                                                onClick={() =>
                                                    handleMessageClick(emailAddress as string, item.messageId)
                                                }
                                            >
                                                <LuMousePointerClick /> Select
                                            </Button>
                                        </Flex>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>

                    {totalPages > 1 && (
                        <Flex justifyContent="center" padding="1rem">
                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onChange={handlePageChange}
                            />
                        </Flex>
                    )}
                </View>
            </Flex>
        </>
    );
};

export default EmailMessages;